import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { useNavigate } from "react-router-dom";

interface ProductFormData {
  name: string;
  sku: string;
  barcode: string;
  description: string;
  price: string;
  cost: string;
  category: string;
  stock: string;
  minStock: string;
  unit: string;
  visibility: {
    pos: boolean;
    online: boolean;
  };
}

const CATEGORIES = ["Electronics", "Clothing", "Food & Beverages", "Home & Garden", "Sports", "Toys"];
const UNITS = ["pcs", "kg", "g", "l", "ml", "box", "pack"];

export default function CreateProductSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    sku: "",
    barcode: "",
    description: "",
    price: "",
    cost: "",
    category: "",
    stock: "",
    minStock: "",
    unit: "pcs",
    visibility: {
      pos: true,
      online: true,
    },
  });

  const [activeTab, setActiveTab] = useState<"general" | "inventory" | "pricing" | "visibility">("general");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const handleInputChange = (field: keyof ProductFormData, value: string | boolean | object) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVisibilityChange = (channel: "pos" | "online") => {
    setFormData((prev) => ({
      ...prev,
      visibility: {
        ...prev.visibility,
        [channel]: !prev.visibility[channel],
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/pos/products/list");
  };

  const tabs = [
    { id: "general" as const, label: t("posProducts.createProduct.generalInfo"), icon: "ri-information-line" },
    { id: "inventory" as const, label: t("posProducts.createProduct.inventory"), icon: "ri-archive-line" },
    { id: "pricing" as const, label: t("posProducts.createProduct.pricing"), icon: "ri-price-tag-3-line" },
    { id: "visibility" as const, label: t("posProducts.createProduct.visibility"), icon: "ri-eye-line" },
  ];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.createProduct.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.createProduct.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/pos/products/list")}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}
          >
            <i className="ri-close-line"></i>
            {t("common.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2"
          >
            <i className="ri-save-line"></i>
            {t("common.save")}
          </button>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`border-b ${themeClasses.border} p-1`}>
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.text} hover:bg-dark-card`
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {activeTab === "general" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.productName")} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder={t("posProducts.createProduct.productNamePlaceholder")}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>SKU</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => handleInputChange("sku", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="PROD-001"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.barcode")}
                </label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => handleInputChange("barcode", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="7501234567890"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.category")}
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange("category", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  <option value="">{t("posProducts.createProduct.selectCategory")}</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.unit")}
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  {UNITS.map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.description")}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none`}
                  placeholder={t("posProducts.createProduct.descriptionPlaceholder")}
                />
              </div>
            </div>
          )}

          {activeTab === "inventory" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.initialStock")}
                </label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => handleInputChange("stock", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.minStock")}
                </label>
                <input
                  type="number"
                  value={formData.minStock}
                  onChange={(e) => handleInputChange("minStock", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="10"
                  min="0"
                />
              </div>

              <div className={`md:col-span-2 p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-500 text-xl mt-0.5"></i>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>{t("posProducts.createProduct.stockInfo")}</p>
                    <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
                      {t("posProducts.createProduct.stockInfoDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "pricing" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.salePrice")} *
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="0.00"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.createProduct.costPrice")}
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => handleInputChange("cost", e.target.value)}
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
              </div>

              {formData.price && formData.cost && (
                <div className={`md:col-span-2 p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
                  <div className="flex items-center justify-between">
                    <span className={themeClasses.textSecondary}>{t("posProducts.createProduct.profitMargin")}</span>
                    <span className="text-primary-500 font-bold text-xl">
                      {((parseFloat(formData.price) - parseFloat(formData.cost)) / parseFloat(formData.price) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "visibility" && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl border ${themeClasses.border} flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <i className="ri-store-2-line text-blue-500 text-xl"></i>
                  </div>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>POS</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      {t("posProducts.createProduct.posVisibilityDesc")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleVisibilityChange("pos")}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.visibility.pos ? "bg-primary-500" : "bg-gray-500"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transform transition-transform ${
                      formData.visibility.pos ? "translate-x-6" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>

              <div className={`p-4 rounded-xl border ${themeClasses.border} flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <i className="ri-global-line text-violet-500 text-xl"></i>
                  </div>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>E-commerce / Web</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      {t("posProducts.createProduct.onlineVisibilityDesc")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleVisibilityChange("online")}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.visibility.online ? "bg-primary-500" : "bg-gray-500"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transform transition-transform ${
                      formData.visibility.online ? "translate-x-6" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
